



































import Vue from 'vue';
import moment from 'moment';
import { buildIDParam, uuidFromURLParam } from '~/utils/urlUtils';
import { RestXReactiveData } from '~/utils/types';

// Services
import AnalyticService from '../../../services/AnalyticService';
import ArticleService from '../../../services/ArticleService';
import { AxiosError } from 'axios';
import VFairService, { VFair, VFairType, VFairPavilion, VFairUser } from '~/services/VFairService';

// Components
import MainLayout from '../../../components/layouts/mainlayout/MainLayout.vue';
import VFairBreadcrumb from './components/VFairBreadcrumb.vue';

// Constants
import URLS from '~/URLS';
import AxiosUtils from '~/utils/axios';

interface Tab {
    to: string;
    text: string;
}

export default Vue.extend({
    name: 'VFair',
    components: {
        MainLayout,
        VFairBreadcrumb,
    },
    data() {
        return {
            dateChecked: false,
            accessChecked: false,
            hasLoggedEvent: false,

            pavilion: null as VFairPavilion | null,
            activityUpdater: null as number | null,
        };
    },
    computed: {
        URLS(): typeof URLS {
            return URLS;
        },
        interfaces(): any {
            return (this as any).$interfaces;
        },
        vFairId(): string {
            return uuidFromURLParam(this.$route.params.id) || '';
        },

        tabs(): Tab[] {
            const titles = {
                booths: this.$t('vfair.pavilionTabs.booths').toString(),
                jobs: this.$t('vfair.pavilionTabs.jobs').toString(),
                webinars: this.$t('vfair.pavilionTabs.webinars').toString(),
                conference: this.$t('vfair.pavilionTabs.conference').toString(),
                chat: this.$t('vfair.pavilionTabs.chat').toString(),
            };

            const hide = (key: keyof typeof titles): boolean => {
                switch (key) {
                    case 'jobs':
                        return this.isSchoolAdmission;
                    case 'conference':
                        return !this.showConference;
                    default:
                        return false;
                }
            };

            return Object.entries(titles).reduce<Tab[]>(
                (acc, [key, text]) =>
                    hide(key as keyof typeof titles)
                        ? acc
                        : [
                              ...acc,
                              {
                                  to: `vfair-${key}`,
                                  id: this.$route.params.id,
                                  text,
                              },
                          ],
                []
            );
        },

        vFair(): Partial<RestXReactiveData<VFair>> {
            if (this.vFairId) {
                return this.interfaces.vfair.id(this.vFairId).get({
                    reactive: true,
                });
            } else {
                return { loading: true };
            }
        },
        vFairType(): VFairType {
            return !!this.vFair.data ? this.vFair.data.vFairType : VFairType.Standard;
        },
        isSchoolAdmission(): boolean {
            return this.vFairType === VFairType.SchoolAdmission;
        },
        articlesHeader(): string {
            if (this.isSchoolAdmission)
                return this.$t('vfair.pavilionTabs.importantInfo').toString();
            else return this.$t('companyDetail.articles.header').toString();
        },
        companiesHeader(): string {
            if (this.isSchoolAdmission)
                return this.$t('vfair.pavilionTabs.degreesPrograms').toString();
            else return this.$t('components.navigation.navigation.companies').toString();
        },
        hackEnabled(): boolean {
            return this.interfaces.vfairModule.getHackEnabled();
        },
        dateLabel(): string {
            if (!this.vFair || !this.vFair.data) return '';
            const startDate = new Date(this.vFair.data.start);
            return startDate.toString();
        },
        articles(): any {
            return this.interfaces.vfairModule.getArticles();
        },
        pavilionQueryParam(): { pavilion_id?: string } {
            return this.pavilion ? { pavilion_id: this.pavilion.id } : {};
        },
        vFairImage(): string | null {
            if (this.vFair.loading) return null;
            if (!this.vFair.data || !this.vFair.data.image) return null;
            return this.vFair.data.image;
        },
        pavilionImage(): string {
            if (!this.pavilion) return this.vFairImage || '';
            return this.pavilion.image || this.vFairImage || '';
        },
        showConference(): boolean {
            const vFairId = this.vFairId;
            const prodVFair = '3aa5fe34-f5a3-4341-8f77-20967860eb20';
            const stagingVFair = 'f791908d-c8cb-4537-87eb-0a4892d4ddca';
            const testVFair = 'f791908d-c8cb-4537-87eb-0a4892d4ddca';
            return vFairId === prodVFair || vFairId === stagingVFair || vFairId === testVFair;
        },
        routerProps(): {
            vfair: { data?: VFair; loading?: boolean };
            vFairType: VFairType;
        } {
            return {
                vfair: this.vFair,
                vFairType: this.vFairType,
            };
        },
        shouldShowVFair(): boolean {
            return this.dateChecked && this.accessChecked;
        },
    },
    methods: {
        getPavilionId(): string {
            let pavilionIdParam = this.$route.query.pavilion_id;
            return Array.isArray(pavilionIdParam) ? pavilionIdParam[0] : pavilionIdParam;
        },

        async fetchPavilion() {
            const vFairId = this.vFairId;
            const pavilionId = this.$route.query.pavilion_id as string;

            this.interfaces.vfairModule.setPavilionId(pavilionId);
            if (!!vFairId && !!pavilionId) {
                const result = await VFairService.getPavilion(vFairId, pavilionId);
                this.pavilion = result.data;
            }
        },

        async checkActiveFair(vfair: VFair) {
            await this.interfaces.vfairuser.user.me
                .vfair_id(this.vFairId)
                .get({ forceUpdate: true })
                .then((res: { data: VFairUser | null }) => {
                    if (!res.data || !res.data.interested) {
                        this.gotoDetails();
                    } else {
                        this.accessChecked = true;
                    }
                });

            const start = moment(vfair.start);
            const end = moment(vfair.end);
            const active = moment().isBetween(start, end);

            if (!active && !this.hackEnabled) {
                this.gotoDetails();
            } else {
                this.dateChecked = true;

                if (!this.hasLoggedEvent) {
                    AnalyticService.vFairEvent(this.vFairId, null, this.getPavilionId() as any);
                    this.hasLoggedEvent = true;
                }
            }
        },
        gotoDetails() {
            this.$router.replace(
                this.URLS.vfairs + '/' + this.$route.params.id + this.URLS.vfairDetails
            );
        },
        updateUserActivity() {
            const vFairId = this.vFairId;
            return VFairService.updateUserActivity(vFairId);
        },
    },
    watch: {
        vFair(newValue: RestXReactiveData<VFair, AxiosError>) {
            if (AxiosUtils.isErrorNotFound(newValue.error)) {
                this.$router.replace(this.URLS.vfairs);
            } else if (newValue.loading === false) this.checkActiveFair(newValue.data);
        },
        shouldShowVFair: {
            handler(newValue: boolean, oldValue: boolean) {
                // Redirect to booth
                if (newValue && !oldValue && this.$route.matched.length === 1) {
                    this.$router.replace(
                        this.URLS.vfairs + '/' + this.$route.params.id + this.URLS.vfairBooths
                    );
                }
            },
        },
    },
    mounted() {
        this.fetchPavilion();

        this.interfaces.vfairModule.setArticles([]);
        if (this.vFair.loading === false && this.vFair.data) this.checkActiveFair(this.vFair.data);

        ArticleService.GetArticlesForVFair(this.vFairId).then(result => {
            this.interfaces.vfairModule.setArticles(result);
        });

        this.activityUpdater = setInterval(this.updateUserActivity, 5000);
    },
    beforeDestroy() {
        if (this.activityUpdater) clearInterval(this.activityUpdater);
    },
});
